/**
 * Mission 68 CREATION boss: fireball projectiles from the bot general portrait to targets.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { playSoundEffect } from './hooks/useSoundEffects';
import { getStrategemCasterGeneralPixelPosition } from './config/casterGeneralStrategemProjectile';
import './ExploreUnknownCreationFireballOverlay.css';

const FIREBALL_IMAGE_SRC = '/images/fireball.png';
const TRAVEL_DURATION_MS = 550;
const FADE_DURATION_MS = 150;

function actionLogEntryKey(entry) {
  if (!entry) return 'no-entry';
  return `${entry.sequence ?? 'no-seq'}:${entry.timestamp ?? 'no-ts'}:${entry.fireballIndex ?? 'fb'}:${entry.targetUnitInstanceId ?? entry.targetPlayerId ?? 'tgt'}`;
}

function findSlotElement(entry, userId) {
  if (typeof document === 'undefined' || !document.querySelector) return null;
  if (entry.targetKind !== 'unit' || !entry.targetZone) return null;
  const isLocalOwner = String(entry.targetPlayerId) === String(userId);
  const prefix = isLocalOwner ? 'your_' : 'opponent_';
  const slotZone = `${prefix}${entry.targetZone}`;
  return document.querySelector(
    `[data-slot-zone="${slotZone}"][data-slot-index="${entry.targetSlotIndex}"]`
  );
}

function findGeneralElement(entry, userId) {
  if (typeof document === 'undefined' || !document.querySelector) return null;
  const isLocal = String(entry.targetPlayerId) === String(userId);
  const zone = isLocal ? 'player_general' : 'opponent_general';
  return document.querySelector(`[data-zone="${zone}"]`);
}

function rectCenter(rect) {
  return { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 };
}

function detectReducedMotion() {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch (_) {
    return false;
  }
}

export default function ExploreUnknownCreationFireballOverlay({ gameState, userId }) {
  const isMission68 = Number(gameState?.exploreUnknownMissionIndex) === 68;
  const seenKeysRef = useRef(new Set());
  const cleanupTimersRef = useRef(new Set());
  const nextAnimIdRef = useRef(1);
  const [fireballs, setFireballs] = useState([]);
  const [reducedMotion, setReducedMotion] = useState(detectReducedMotion);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    let mq;
    try {
      mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    } catch (_) {
      return undefined;
    }
    const handler = (e) => setReducedMotion(!!e.matches);
    if (typeof mq.addEventListener === 'function') {
      mq.addEventListener('change', handler);
      return () => mq.removeEventListener('change', handler);
    }
    return undefined;
  }, []);

  useEffect(() => {
    return () => {
      for (const timerId of cleanupTimersRef.current) {
        clearTimeout(timerId);
      }
      cleanupTimersRef.current.clear();
    };
  }, []);

  const scheduleRemoval = useCallback((animId) => {
    const timerId = setTimeout(() => {
      setFireballs((prev) => prev.filter((f) => f.id !== animId));
      cleanupTimersRef.current.delete(timerId);
    }, TRAVEL_DURATION_MS + FADE_DURATION_MS + 80);
    cleanupTimersRef.current.add(timerId);
  }, []);

  useEffect(() => {
    if (!isMission68) return;
    const log = Array.isArray(gameState?.actionLog) ? gameState.actionLog : [];
    const entries = log.filter((e) => e && e.type === 'eu-creation-fireball-fired' && e.targetKind !== 'skipped');
    if (entries.length === 0) return;

    const botPlayerId = gameState.players?.find((p) => String(p) === 'bot') ? 'bot' : 'bot';
    const fromGeneral = getStrategemCasterGeneralPixelPosition(userId, botPlayerId, true);
    if (!fromGeneral) return;

    const newItems = [];

    for (const entry of entries) {
      const key = actionLogEntryKey(entry);
      if (seenKeysRef.current.has(key)) continue;
      seenKeysRef.current.add(key);

      if (entry.soundPath) {
        playSoundEffect(entry.soundPath, 0.85);
      }

      let targetEl = null;
      if (entry.targetKind === 'unit') {
        targetEl = findSlotElement(entry, userId);
      } else if (entry.targetKind === 'general') {
        targetEl = findGeneralElement(entry, userId);
      }
      if (!targetEl) continue;

      const targetCenter = rectCenter(targetEl.getBoundingClientRect());
      const dx = targetCenter.x - fromGeneral.x;
      const dy = targetCenter.y - fromGeneral.y;
      const angleDeg = Math.atan2(dy, dx) * (180 / Math.PI) + 90;
      const dist = Math.hypot(dx, dy);

      const animId = nextAnimIdRef.current;
      nextAnimIdRef.current += 1;

      newItems.push({
        id: animId,
        startX: fromGeneral.x,
        startY: fromGeneral.y,
        dx: reducedMotion ? 0 : dx,
        dy: reducedMotion ? 0 : dy,
        angleDeg,
        dist,
      });
    }

    if (newItems.length > 0) {
      setFireballs((prev) => [...prev, ...newItems]);
      newItems.forEach((item) => scheduleRemoval(item.id));
    }
  }, [gameState, userId, isMission68, reducedMotion, scheduleRemoval]);

  if (!isMission68) return null;
  if (typeof document === 'undefined' || !document.body) return null;
  if (fireballs.length === 0) return null;

  return createPortal(
    <div className="explore-unknown-creation-fireball-overlay" aria-hidden>
      {fireballs.map((fb) => (
        <img
          key={fb.id}
          src={FIREBALL_IMAGE_SRC}
          alt=""
          className={`explore-unknown-creation-fireball-projectile${reducedMotion ? ' is-reduced-motion' : ''}`}
          style={{
            left: `${fb.startX}px`,
            top: `${fb.startY}px`,
            ['--eu-fireball-dx']: `${fb.dx}px`,
            ['--eu-fireball-dy']: `${fb.dy}px`,
            ['--eu-fireball-rotate']: `${fb.angleDeg}deg`,
            ['--eu-fireball-travel-ms']: `${TRAVEL_DURATION_MS}ms`,
          }}
        />
      ))}
    </div>,
    document.body
  );
}
